﻿/*
 * Purpose: Compose Login UI using stateless controls.
 * Boundary: Layout only; no business logic; delegates state/actions to hook.
 * Invariants:
 * - Consumes object h from useAuthForm with state and handlers.
 * - Uses PhoneNumber for cell input and LoginPassword for password.
 * - ASCII only; no Unicode in comments or strings
 */
// Imports
import { useMemo } from 'react';

import Chip from '../authform-chips';
import PhoneNumber from '../../../contact/phonenumber';
import { IdField, LoginPassword } from '../authform-controls';
import { PhoneContext } from '../../../contact/phonenumber';

// Component LoginLayout:
// Stateless layout consuming hook output
// Receives hook output (state + handlers) from useAuthForm
export default function LoginLayout(h) {

    // Rendering - outer wrapper and page title
    return (
        <div className='space-y-3 md:space-y-6' aria-labelledby='login-title'>
            {/* Title - shows Login */}
            <h1 id='login-title' className='text-base md:text-xl font-semibold'>
                Login
            </h1>
            {/* Top status - chips row and hint text */}
            <div className='flex flex-col gap-1'>
                {/* Chip row - required field status */}
                <div className='flex flex-wrap items-center gap-1.5 md:gap-2'>
                    <Chip ok={h.proceedRequired.id} warnWhenMissing>ID</Chip>
                    <Chip ok={h.proceedRequired.cell} warnWhenMissing>Cell</Chip>
                    <Chip ok={h.proceedRequired.password} warnWhenMissing>Password</Chip>
                </div>
                {/* Hint - below chips, shows missing list or ready message */}
                <p className={`mt-[4px] text-[12px] ${h.isProceedReady ? 'text-emerald-300' : 'text-orange-300'}`}>
                    {h.isProceedReady ? 'All required fields completed' : `Missing: ${h.missingList().join(', ')}`}
                </p>
            </div>
            {/* Main form container */}
            <div className='relative rounded-xl bg-neutral-950 ring-1 ring-neutral-800 p-2.5 md:p-5'>
                {/* Form - shared container */}
                <form onSubmit={h.requestLogin} className='grid gap-3 md:gap-4' noValidate>
                    {/* ID Number */}
                    <IdField
                        id='login-id'
                        value={h.form.idNumber}
                        onChange={(e) => h.setField('idNumber', e.target.value.replace(/\D/g, ''))}
                    />
                    <PhoneContext.Provider value={useMemo(() =>
                        ({ setFullNumber: h.setCellNumber, setIsValid: h.setIsPhoneValid, setCountryCode: h.setCountryCode }),
                        [h.setCellNumber, h.setIsPhoneValid, h.setCountryCode])}>
                        <PhoneNumber
                            enableCountrySelector={true}
                            defaultCountry='ZA'
                            showValidationLabel={false}
                        />
                    </PhoneContext.Provider>
                    <LoginPassword
                        id='login-password'
                        label='Password'
                        value={h.form.password}
                        onChange={(e) => {
                            const newPassword = e.target.value;
                            h.setField('password', newPassword);
                            h.setPasswordState({ isValid: !!newPassword, password: newPassword });
                        }}
                        autoComplete='current-password'
                    />
                    <hr className='my-0.5 md:my-1 border-neutral-800' />
                    {/* Submit or warning */}
                    {h.isLoginReady ? (
                        <button
                            id='btn-login'
                            type='submit'
                            className='h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 transition text-white font-medium disabled:opacity-60'
                            disabled={h.isPending || h.showTransition}
                        >
                            Login
                        </button>
                    ) : (
                        <p className='h-10 flex items-center justify-center rounded-lg bg-orange-900/20 text-sm text-orange-300'>
                            Login requirements not met
                        </p>
                    )}
                    {/* Forgot Password link */}
                    <button
                        id='btn-forgot-password'
                        type='button'
                        className='mt-2 text-sm text-amber-700 hover:text-orange-500 transition'
                        onClick={() => h.setMode('forgot-password')}
                    >
                        Forgotten my password
                    </button>
                </form>

                {/* Transition overlay -- consistent with register and legal guardian pattern */}
                {h.showTransition && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center bg-neutral-900/95 backdrop-blur-sm rounded-xl"
                         style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
                        <div className="max-w-md mx-4 p-6 bg-neutral-800 rounded-xl ring-1 ring-emerald-600 shadow-2xl">
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0">
                                    <svg className="w-6 h-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm md:text-base text-neutral-200 leading-relaxed">
                                        {h.transitionMessage}
                                    </p>
                                </div>
                            </div>
                            <div className="mt-4 h-1 bg-neutral-700 rounded-full overflow-hidden">
                                <div className="h-full bg-emerald-500 rounded-full"
                                     style={{ animation: 'progress 1.5s ease-in-out forwards' }} />
                            </div>
                        </div>
                    </div>
                )}
                {/* Status message - polite live region */}
                {h.status && (
                    <p className='mt-4 text-sm text-neutral-400' role='status' aria-live='polite'>
                        {h.status}
                    </p>
                )}
            </div>
        </div>
    );
}